/*!
 * scopeRidge.js — a scope's recent past as a stack of lines
 * © 2026 Rob Canning — GPLv3
 *
 *     viz(s1, view:ridge)              the wave, a line a moment, stacked
 *     viz(s1, view:waterfall)          the spectrum, the same way
 *     viz(s1, view:ridge, rows:40, span:4)   40 lines over the last 4 beats
 *
 * The newest line is at the bottom, in front; the older ones go up and
 * back, and the oldest leaves at the top. Each line is filled below with
 * the stage's colour, so a line in front hides those behind it where it
 * rises -- a ridgeline. The stack
 * scrolls smoothly between lines.
 *
 * A wave line starts where the wave crosses upward, so a steady note keeps
 * its place line after line, and its height is scaled to the loudest of
 * the last few lines, so a note dying away is seen shrinking. A spectrum
 * line is low frequencies on the left, on a log scale, as the spectrum view.
 *
 * Only a line's points are worked out, once, when it is taken; a frame
 * moves the lines. In the score each is an SVG path, moved by its
 * transform; in a console box they are painted on its canvas (a big box
 * of moving SVG costs more to raster every frame: scope.js).
 */

import { levelAt } from "./scopeViews.js";

const SVG_NS = "http://www.w3.org/2000/svg";

export const RIDGE_VIEWS = new Set(["ridge", "waterfall"]);
export const RIDGE_ROWS = 24;
export const RIDGE_SPAN = 2;                  // beats of history, by default

/**
 * Where the lines go in the box: the newest's baseline `yb`, the gap
 * between them, `amp` how far a line reaches from its baseline and `depth`
 * how far its fill goes below it. A wave swings both ways about its
 * baseline and reaches 3 lines up; a spectrum rises 6 lines from its.
 */
export function ridgeLayout(view, draw, rows) {
  const H = draw.height, n = Math.max(2, rows);
  if (view === "waterfall") {
    const amp = (6 * H) / (n + 5);
    return { yb: draw.y + H, gap: (H - amp) / (n - 1), amp, depth: 0 };
  }
  const amp = (3 * H) / (n + 5);
  return { yb: draw.y + H - amp, gap: (H - 2 * amp) / (n - 1), amp, depth: amp };
}

/** How many points a line has across the box. */
const pointsAcross = draw => Math.max(8, Math.min(400, Math.round(draw.width / 2)));

/** The first upward crossing in the first half of the frame (0 if none). */
export function triggerIndex(data) {
  const half = data.length >> 1;
  for (let i = 1; i < half; i++) if (data[i - 1] <= 0 && data[i] > 0) return i;
  return 0;
}

/**
 * A wave line: [x, dy, x, dy ...], dy from the baseline (up is negative),
 * half a frame from its trigger. `state.peak` is the loudest of the recent
 * lines, falling slowly: each line is scaled to it.
 */
export function waveRow(data, draw, amp, state) {
  const i0 = triggerIndex(data), len = data.length >> 1;
  let peak = 0;
  for (let i = i0; i < i0 + len; i++) { const a = Math.abs(data[i]); if (a > peak) peak = a; }
  state.peak = Math.max(peak, 0.01, (state.peak || 0) * 0.9);
  const n = pointsAcross(draw), k = amp / state.peak;
  const row = new Float32Array(n * 2);
  for (let j = 0; j < n; j++) {
    row[2 * j] = draw.x + (j / (n - 1)) * draw.width;
    row[2 * j + 1] = -data[i0 + Math.floor((j * len) / n)] * k;
  }
  return row;
}

/**
 * A spectrum line: the level per frequency, low on the left, rising from
 * the baseline. Cubed: on the analyser's dB scale a saw's harmonics all sit
 * near the top, a flat plateau line after line; cubed, each is a peak.
 */
export function spectrumRow(freq, draw, amp, sampleRate) {
  const n = pointsAcross(draw);
  const row = new Float32Array(n * 2);
  for (let j = 0; j < n; j++) {
    row[2 * j] = draw.x + (j / (n - 1)) * draw.width;
    row[2 * j + 1] = -(levelAt(freq, j / (n - 1), sampleRate) ** 3) * amp;
  }
  return row;
}

/**
 * The baseline of the line `age` lines old (0 the newest), `frac` of the
 * way to the next line: the stack moves up between lines.
 */
export const baselineOf = (lay, age, frac) => lay.yb - (age + frac) * lay.gap;

/** A line as SVG path data: along it, and closed under it by `depth` (its fill). */
function rowPaths(row, depth) {
  const n = row.length / 2;
  let line = "";
  for (let j = 0; j < n; j++) line += `${j ? "L" : "M"}${row[2 * j].toFixed(1)},${row[2 * j + 1].toFixed(1)}`;
  const x0 = row[0].toFixed(1), x1 = row[row.length - 2].toFixed(1), y = depth.toFixed(1);
  return { line, fill: `M${x0},${y}L${line.slice(1)}L${x1},${y}Z` };
}

// ---- the colour behind the lines ----------------------------------------

const solid = c => c && c !== "none" && c !== "transparent" && !/rgba\([^)]*,\s*0\)$/.test(c);

function invertColor(c) {
  const m = String(c).match(/rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/);
  return m ? `rgb(${255 - m[1]},${255 - m[2]},${255 - m[3]})` : "#000";
}

/**
 * What the lines are filled with: none when the box was given fill:none
 * (see-through lines), the box's own fill (viz(s1, fill:#123)), else the
 * stage's colour -- the score's background, or the page's. Dark
 * mode inverts the page, and undoes it on what the console makes
 * (livecode.css): there the stage is drawn in its colour as seen.
 */
export function ridgeFill(box, group) {
  if (box?.dataset?.vizFill === "none") return "none";
  const own = box?.getAttribute?.("fill");
  if (solid(own)) return own;
  let c = document.querySelector("#scoreContainer svg #background, #scoreContainer svg #layer-background rect")?.getAttribute("fill");
  for (let n = document.getElementById("scoreContainer"); !solid(c) && n; n = n.parentElement) {
    c = getComputedStyle(n).backgroundColor;
  }
  if (!solid(c)) c = "rgb(255,255,255)";
  const undone = document.documentElement.style.filter.includes("invert")
    && group?.closest?.("#oscilla-live-audio-group, #oscilla-live-shapes");
  return undone ? invertColor(c) : c;
}

// ---- drawing ------------------------------------------------------------

/** Show or hide the ridge's surfaces (the SVG lines, the canvas). */
export function showRidge(handle, which) {
  if (handle._ridgeG) handle._ridgeG.style.display = which === "ridge" ? "" : "none";
  if (handle._ridgeCv) handle._ridgeCv.fo.style.display = which === "ridgeCanvas" ? "" : "none";
}

/**
 * The lines in the score, as SVG paths. A path is made for a line when it
 * is taken and then only moved; the oldest's path is used again for the
 * newest, which goes in front.
 */
export function paintRidgeSvg(handle, st, lay, frac, style) {
  let g = handle._ridgeG;
  if (!g) {
    g = handle._ridgeG = document.createElementNS(SVG_NS, "g");
    g.setAttribute("class", "oscilla-scope-ridge");
    g.setAttribute("stroke-linejoin", "round");
    handle.group.appendChild(g);
  }
  // the newest line, made now: the oldest one's elements, brought to the front.
  // A line is its fill (no stroke: the closing edges would show) and its stroke.
  if (st.fresh) {
    st.fresh = false;
    let r = g.childElementCount >= st.rows.length ? g.firstElementChild : null;
    if (!r) {
      r = document.createElementNS(SVG_NS, "g");
      const fill = document.createElementNS(SVG_NS, "path");
      fill.setAttribute("stroke", "none");
      const line = document.createElementNS(SVG_NS, "path");
      line.setAttribute("fill", "none");
      line.setAttribute("vector-effect", "non-scaling-stroke");
      r.append(fill, line);
    }
    const { line, fill } = rowPaths(st.rows[st.rows.length - 1], lay.depth);
    r.firstElementChild.setAttribute("d", fill);
    r.lastElementChild.setAttribute("d", line);
    g.appendChild(r);
    while (g.childElementCount > st.rows.length) g.firstElementChild.remove();
  }
  g.setAttribute("stroke", style.color);
  g.setAttribute("stroke-width", style.width);
  g.setAttribute("fill", style.fill);
  const kids = g.children, n = kids.length;
  for (let i = 0; i < n; i++) {
    const age = n - 1 - i;
    kids[i].setAttribute("transform", `translate(0 ${baselineOf(lay, age, frac).toFixed(1)})`);
    // the oldest leaves as the next comes in
    kids[i].style.opacity = age === st.max - 1 ? String(1 - frac) : "";
  }
}

/** The lines in a console box: painted on its canvas each frame, oldest first. */
export function paintRidgeCanvas(canvas, area, st, lay, frac, style) {
  const g = canvas.getContext("2d");
  const sx = canvas.width / area.width, sy = canvas.height / area.height;
  const dpr = window.devicePixelRatio || 1;
  g.clearRect(0, 0, canvas.width, canvas.height);
  g.strokeStyle = style.color;
  g.fillStyle = style.fill;
  g.lineWidth = style.width * dpr;
  g.lineJoin = "round";
  const n = st.rows.length;
  for (let i = 0; i < n; i++) {
    const row = st.rows[i], age = n - 1 - i;
    const base = baselineOf(lay, age, frac);
    const X = x => (x - area.x) * sx, Y = y => (base + y - area.y) * sy;
    g.globalAlpha = age === st.max - 1 ? 1 - frac : 1;
    g.beginPath();
    g.moveTo(X(row[0]), Y(lay.depth));
    for (let j = 0; j < row.length; j += 2) g.lineTo(X(row[j]), Y(row[j + 1]));
    g.lineTo(X(row[row.length - 2]), Y(lay.depth));
    g.closePath();
    if (style.fill !== "none") g.fill();
    // the stroke along the line only, not its closing edges
    g.beginPath();
    g.moveTo(X(row[0]), Y(row[1]));
    for (let j = 2; j < row.length; j += 2) g.lineTo(X(row[j]), Y(row[j + 1]));
    g.stroke();
  }
  g.globalAlpha = 1;
}
